import { Activity, SearchGroupNameEnumv2 } from 'api/gen'
import {
  categoryIllustrationUrls,
  CategoryIllustrationUrl,
} from 'features/search/constants/categoryIllustrationUrls'
import { MAP_ACTIVITY_TO_LABEL } from 'libs/parsers/activity'
import { BackgroundColorKey, BorderColorKey, IllustrationColorKey } from 'theme/types'
import { ArtsCrafts } from 'ui/svg/icons/categories/ArtsCrafts'
import { Books } from 'ui/svg/icons/categories/Books'
import { CDVinylsOnlineMusic } from 'ui/svg/icons/categories/CDVinylsOnlineMusic'
import { ConcertsFestivals } from 'ui/svg/icons/categories/ConcertsFestivals'
import { ConferencesMeetings } from 'ui/svg/icons/categories/ConferencesMeetings'
import { FilmsSeriesCinema } from 'ui/svg/icons/categories/FilmsSeriesCinema'
import { GamesVideoGames } from 'ui/svg/icons/categories/GamesVideoGames'
import { LibrariesMediaLibraries } from 'ui/svg/icons/categories/LibrariesMediaLibraries'
import { MediaPress } from 'ui/svg/icons/categories/MediaPress'
import { MuseumCulturalVisits } from 'ui/svg/icons/categories/MuseumCulturalVisits'
import { MusicalInstruments } from 'ui/svg/icons/categories/MusicalInstruments'
import { OnlineEvents } from 'ui/svg/icons/categories/OnlineEvents'
import { Shows } from 'ui/svg/icons/categories/Shows'
import { YouthCards } from 'ui/svg/icons/categories/YouthCards'
import { categoriesIcons } from 'ui/svg/icons/exports/categoriesIcons'
import { AccessibleIcon } from 'ui/svg/icons/types'

export enum DATE_FILTER_OPTIONS {
  TODAY = 'today',
  CURRENT_WEEK = 'currentWeek',
  CURRENT_WEEK_END = 'currentWeekEnd',
  USER_PICK = 'picked',
}

export enum CategoriesModalView {
  CATEGORIES = 'CATEGORIES',
  NATIVE_CATEGORIES = 'NATIVE_CATEGORIES',
  GENRES = 'GENRES',
}

export const SearchCategoriesIllustrations = {
  ArtsCrafts,
  Books,
  CDVinylsOnlineMusic,
  ConcertsFestivals,
  ConferencesMeetings,
  FilmsSeriesCinema,
  GamesVideoGames,
  LibrariesMediaLibraries,
  MediaPress,
  MuseumCulturalVisits,
  MusicalInstruments,
  OnlineEvents,
  Shows,
  YouthCards,
}

export type CategoryCriteria = {
  icon: React.FC<AccessibleIcon>
  illustration: React.FC<AccessibleIcon>
  illustrationUrl?: never
  facetFilter: SearchGroupNameEnumv2
  searchLandingPosition: number
  filterModalPosition: number
  fillColor: BackgroundColorKey
  borderColor: BorderColorKey
}

export type NewCategoryCriteria = {
  icon: React.FC<AccessibleIcon>
  illustrationUrl?: CategoryIllustrationUrl
  facetFilter: SearchGroupNameEnumv2
  searchLandingPosition: number
  filterModalPosition: number
  fillColor: IllustrationColorKey
  borderColor: BorderColorKey
}

type CategoryCriteriaWithNone = {
  [category in SearchGroupNameEnumv2]: category extends SearchGroupNameEnumv2.NONE
    ? {
        icon: React.FC<AccessibleIcon>
        illustration: undefined
        facetFilter: SearchGroupNameEnumv2
        searchLandingPosition: undefined
        filterModalPosition: undefined
        fillColor: undefined
        borderColor: undefined
      }
    : CategoryCriteria
}

type NewCategoryCriteriaWithNone = {
  [category in SearchGroupNameEnumv2]: category extends SearchGroupNameEnumv2.NONE
    ? {
        icon: React.FC<AccessibleIcon>
        illustration: undefined
        facetFilter: SearchGroupNameEnumv2
        searchLandingPosition: undefined
        filterModalPosition: undefined
        fillColor: undefined
        borderColor: undefined
      }
    : NewCategoryCriteria
}

export const CATEGORY_CRITERIA: CategoryCriteriaWithNone = {
  [SearchGroupNameEnumv2.NONE]: {
    icon: categoriesIcons.All,
    illustration: undefined,
    facetFilter: SearchGroupNameEnumv2.NONE,
    searchLandingPosition: undefined,
    filterModalPosition: undefined,
    borderColor: undefined,
    fillColor: undefined,
  },
  [SearchGroupNameEnumv2.CARTES_JEUNES]: {
    icon: categoriesIcons.Card,
    illustration: SearchCategoriesIllustrations.YouthCards,
    facetFilter: SearchGroupNameEnumv2.CARTES_JEUNES,
    searchLandingPosition: 11,
    filterModalPosition: 1,
    borderColor: 'decorative03',
    fillColor: 'decorative03',
  },
  [SearchGroupNameEnumv2.CONCERTS_FESTIVALS]: {
    icon: categoriesIcons.Conference,
    illustration: SearchCategoriesIllustrations.ConcertsFestivals,
    facetFilter: SearchGroupNameEnumv2.CONCERTS_FESTIVALS,
    searchLandingPosition: 1,
    filterModalPosition: 2,
    borderColor: 'decorative03',
    fillColor: 'decorative03',
  },
  [SearchGroupNameEnumv2.CINEMA]: {
    icon: categoriesIcons.Cinema,
    illustration: SearchCategoriesIllustrations.FilmsSeriesCinema,
    facetFilter: SearchGroupNameEnumv2.CINEMA,
    searchLandingPosition: 2,
    filterModalPosition: 3,
    borderColor: 'decorative01',
    fillColor: 'decorative01',
  },
  [SearchGroupNameEnumv2.FILMS_DOCUMENTAIRES_SERIES]: {
    icon: categoriesIcons.Cinema,
    illustration: SearchCategoriesIllustrations.FilmsSeriesCinema,
    facetFilter: SearchGroupNameEnumv2.FILMS_DOCUMENTAIRES_SERIES,
    searchLandingPosition: 3,
    filterModalPosition: 4,
    borderColor: 'decorative02',
    fillColor: 'decorative02',
  },
  [SearchGroupNameEnumv2.LIVRES]: {
    icon: categoriesIcons.Book,
    illustration: SearchCategoriesIllustrations.Books,
    facetFilter: SearchGroupNameEnumv2.LIVRES,
    searchLandingPosition: 4,
    filterModalPosition: 5,
    borderColor: 'decorative05',
    fillColor: 'decorative05',
  },
  [SearchGroupNameEnumv2.MUSIQUE]: {
    icon: categoriesIcons.Disk,
    illustration: SearchCategoriesIllustrations.CDVinylsOnlineMusic,
    facetFilter: SearchGroupNameEnumv2.MUSIQUE,
    searchLandingPosition: 5,
    filterModalPosition: 6,
    borderColor: 'decorative04',
    fillColor: 'decorative04',
  },
  [SearchGroupNameEnumv2.ARTS_LOISIRS_CREATIFS]: {
    icon: categoriesIcons.Palette,
    illustration: SearchCategoriesIllustrations.ArtsCrafts,
    facetFilter: SearchGroupNameEnumv2.ARTS_LOISIRS_CREATIFS,
    searchLandingPosition: 6,
    filterModalPosition: 7,
    borderColor: 'decorative01',
    fillColor: 'decorative01',
  },
  [SearchGroupNameEnumv2.SPECTACLES]: {
    icon: categoriesIcons.Show,
    illustration: SearchCategoriesIllustrations.Shows,
    facetFilter: SearchGroupNameEnumv2.SPECTACLES,
    searchLandingPosition: 7,
    filterModalPosition: 8,
    borderColor: 'decorative05',
    fillColor: 'decorative05',
  },
  [SearchGroupNameEnumv2.MUSEES_VISITES_CULTURELLES]: {
    icon: categoriesIcons.Museum,
    illustration: SearchCategoriesIllustrations.MuseumCulturalVisits,
    facetFilter: SearchGroupNameEnumv2.MUSEES_VISITES_CULTURELLES,
    searchLandingPosition: 8,
    filterModalPosition: 9,
    borderColor: 'decorative03',
    fillColor: 'decorative03',
  },
  [SearchGroupNameEnumv2.JEUX_JEUX_VIDEOS]: {
    icon: categoriesIcons.VideoGame,
    illustration: SearchCategoriesIllustrations.GamesVideoGames,
    facetFilter: SearchGroupNameEnumv2.JEUX_JEUX_VIDEOS,
    searchLandingPosition: 9,
    filterModalPosition: 10,
    borderColor: 'decorative04',
    fillColor: 'decorative04',
  },
  [SearchGroupNameEnumv2.MEDIA_PRESSE]: {
    icon: categoriesIcons.Press,
    illustration: SearchCategoriesIllustrations.MediaPress,
    facetFilter: SearchGroupNameEnumv2.MEDIA_PRESSE,
    searchLandingPosition: 10,
    filterModalPosition: 11,
    borderColor: 'decorative02',
    fillColor: 'decorative02',
  },
  [SearchGroupNameEnumv2.RENCONTRES_CONFERENCES]: {
    icon: categoriesIcons.Microphone,
    illustration: SearchCategoriesIllustrations.ConferencesMeetings,
    facetFilter: SearchGroupNameEnumv2.RENCONTRES_CONFERENCES,
    searchLandingPosition: 12,
    filterModalPosition: 12,
    borderColor: 'decorative01',
    fillColor: 'decorative01',
  },
  [SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE]: {
    icon: categoriesIcons.LiveEvent,
    illustration: SearchCategoriesIllustrations.OnlineEvents,
    searchLandingPosition: 13,
    filterModalPosition: 13,
    facetFilter: SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE,
    borderColor: 'decorative02',
    fillColor: 'decorative02',
  },
}

export const NEW_CATEGORY_CRITERIA: NewCategoryCriteriaWithNone = {
  [SearchGroupNameEnumv2.NONE]: {
    icon: categoriesIcons.All,
    illustration: undefined,
    facetFilter: SearchGroupNameEnumv2.NONE,
    searchLandingPosition: undefined,
    filterModalPosition: undefined,
    borderColor: undefined,
    fillColor: undefined,
  },
  [SearchGroupNameEnumv2.CARTES_JEUNES]: {
    icon: categoriesIcons.Card,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.CARTES_JEUNES],
    facetFilter: SearchGroupNameEnumv2.CARTES_JEUNES,
    searchLandingPosition: 11,
    filterModalPosition: 1,
    borderColor: 'decorative01',
    fillColor: 'pending01',
  },
  [SearchGroupNameEnumv2.CONCERTS_FESTIVALS]: {
    icon: categoriesIcons.Conference,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.CONCERTS_FESTIVALS],
    facetFilter: SearchGroupNameEnumv2.CONCERTS_FESTIVALS,
    searchLandingPosition: 1,
    filterModalPosition: 2,
    borderColor: 'decorative01',
    fillColor: 'pending01',
  },
  [SearchGroupNameEnumv2.CINEMA]: {
    icon: categoriesIcons.Cinema,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.CINEMA],
    facetFilter: SearchGroupNameEnumv2.CINEMA,
    searchLandingPosition: 2,
    filterModalPosition: 3,
    borderColor: 'decorative02',
    fillColor: 'information04',
  },
  [SearchGroupNameEnumv2.FILMS_DOCUMENTAIRES_SERIES]: {
    icon: categoriesIcons.Cinema,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.FILMS_DOCUMENTAIRES_SERIES],
    facetFilter: SearchGroupNameEnumv2.FILMS_DOCUMENTAIRES_SERIES,
    searchLandingPosition: 3,
    filterModalPosition: 4,
    borderColor: 'brandPrimary',
    fillColor: 'information03',
  },
  [SearchGroupNameEnumv2.LIVRES]: {
    icon: categoriesIcons.Book,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.LIVRES],
    facetFilter: SearchGroupNameEnumv2.LIVRES,
    searchLandingPosition: 4,
    filterModalPosition: 5,
    borderColor: 'decorative05',
    fillColor: 'negative01',
  },
  [SearchGroupNameEnumv2.MUSIQUE]: {
    icon: categoriesIcons.Disk,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.MUSIQUE],
    facetFilter: SearchGroupNameEnumv2.MUSIQUE,
    searchLandingPosition: 5,
    filterModalPosition: 6,
    borderColor: 'decorative03',
    fillColor: 'positive01',
  },
  [SearchGroupNameEnumv2.ARTS_LOISIRS_CREATIFS]: {
    icon: categoriesIcons.Palette,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.ARTS_LOISIRS_CREATIFS],
    facetFilter: SearchGroupNameEnumv2.ARTS_LOISIRS_CREATIFS,
    searchLandingPosition: 6,
    filterModalPosition: 7,
    borderColor: 'decorative02',
    fillColor: 'information04',
  },
  [SearchGroupNameEnumv2.SPECTACLES]: {
    icon: categoriesIcons.Show,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.SPECTACLES],
    facetFilter: SearchGroupNameEnumv2.SPECTACLES,
    searchLandingPosition: 7,
    filterModalPosition: 8,
    borderColor: 'decorative05',
    fillColor: 'negative01',
  },
  [SearchGroupNameEnumv2.MUSEES_VISITES_CULTURELLES]: {
    icon: categoriesIcons.Museum,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.MUSEES_VISITES_CULTURELLES],
    facetFilter: SearchGroupNameEnumv2.MUSEES_VISITES_CULTURELLES,
    searchLandingPosition: 8,
    filterModalPosition: 9,
    borderColor: 'decorative01',
    fillColor: 'pending01',
  },
  [SearchGroupNameEnumv2.JEUX_JEUX_VIDEOS]: {
    icon: categoriesIcons.VideoGame,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.JEUX_JEUX_VIDEOS],
    facetFilter: SearchGroupNameEnumv2.JEUX_JEUX_VIDEOS,
    searchLandingPosition: 9,
    filterModalPosition: 10,
    borderColor: 'brandPrimary',
    fillColor: 'information03',
  },
  [SearchGroupNameEnumv2.MEDIA_PRESSE]: {
    icon: categoriesIcons.Press,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.MEDIA_PRESSE],
    facetFilter: SearchGroupNameEnumv2.MEDIA_PRESSE,
    searchLandingPosition: 10,
    filterModalPosition: 11,
    borderColor: 'decorative02',
    fillColor: 'information04',
  },
  [SearchGroupNameEnumv2.RENCONTRES_CONFERENCES]: {
    icon: categoriesIcons.Microphone,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.RENCONTRES_CONFERENCES],
    facetFilter: SearchGroupNameEnumv2.RENCONTRES_CONFERENCES,
    searchLandingPosition: 12,
    filterModalPosition: 12,
    borderColor: 'decorative03',
    fillColor: 'positive01',
  },
  [SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE]: {
    icon: categoriesIcons.LiveEvent,
    illustrationUrl: categoryIllustrationUrls[SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE],
    searchLandingPosition: 13,
    filterModalPosition: 13,
    facetFilter: SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE,
    borderColor: 'brandPrimary',
    fillColor: 'information03',
  },
}

type ActivityCriteria = { ALL: { label: string; facetFilter: '' } } & {
  [activity in Activity]: { label: string; facetFilter: activity }
}

export const ACTIVITY_CRITERIA: ActivityCriteria = {
  ALL: {
    label: 'Tous les types de lieu',
    facetFilter: '',
  },
  [Activity.ART_GALLERY]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.ART_GALLERY],
    facetFilter: Activity.ART_GALLERY,
  },
  [Activity.ARTS_CENTRE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.ARTS_CENTRE],
    facetFilter: Activity.ARTS_CENTRE,
  },
  [Activity.BOOKSTORE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.BOOKSTORE],
    facetFilter: Activity.BOOKSTORE,
  },
  [Activity.CINEMA]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.CINEMA],
    facetFilter: Activity.CINEMA,
  },
  [Activity.COMMUNITY_CENTRE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.COMMUNITY_CENTRE],
    facetFilter: Activity.COMMUNITY_CENTRE,
  },
  [Activity.CREATIVE_ARTS_STORE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.CREATIVE_ARTS_STORE],
    facetFilter: Activity.CREATIVE_ARTS_STORE,
  },
  [Activity.CULTURAL_CENTRE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.CULTURAL_CENTRE],
    facetFilter: Activity.CULTURAL_CENTRE,
  },
  [Activity.DISTRIBUTION_STORE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.DISTRIBUTION_STORE],
    facetFilter: Activity.DISTRIBUTION_STORE,
  },
  [Activity.FESTIVAL]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.FESTIVAL],
    facetFilter: Activity.FESTIVAL,
  },
  [Activity.HERITAGE_SITE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.HERITAGE_SITE],
    facetFilter: Activity.HERITAGE_SITE,
  },
  [Activity.LIBRARY]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.LIBRARY],
    facetFilter: Activity.LIBRARY,
  },
  [Activity.MUSEUM]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.MUSEUM],
    facetFilter: Activity.MUSEUM,
  },
  [Activity.MUSIC_INSTRUMENT_STORE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.MUSIC_INSTRUMENT_STORE],
    facetFilter: Activity.MUSIC_INSTRUMENT_STORE,
  },
  [Activity.OTHER]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.OTHER],
    facetFilter: Activity.OTHER,
  },
  [Activity.PERFORMANCE_HALL]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.PERFORMANCE_HALL],
    facetFilter: Activity.PERFORMANCE_HALL,
  },
  [Activity.RECORD_STORE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.RECORD_STORE],
    facetFilter: Activity.RECORD_STORE,
  },
  [Activity.TOURIST_INFORMATION_CENTRE]: {
    label: MAP_ACTIVITY_TO_LABEL[Activity.TOURIST_INFORMATION_CENTRE],
    facetFilter: Activity.TOURIST_INFORMATION_CENTRE,
  },
}

export enum FilterBehaviour {
  SEARCH = 'Search',
  APPLY_WITHOUT_SEARCHING = 'ApplyWithoutSearching',
}
